import React, { useState, useEffect, useCallback } from 'react';

import {

  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  Chip,
  useTheme,
  alpha,
  TextField

} from '@mui/material';

import {

  Upload as UploadIcon,
  Delete as DeleteIcon,
  Description as ScriptIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Add as AddIcon

} from '@mui/icons-material';

import { getProjectScripts, deleteScript, createBlankScript } from '../services/scriptService';

import ScriptUploader from './ScriptUploader';

import ScriptViewer from './ScriptViewer';

import ScreenplayAnalysisManager from './ScreenplayAnalysisManager';

import { usePermissions } from '../contexts/PermissionsContext';

const ScriptManager = ({ projectId, projectName, onScriptSelect }) => {

  const theme = useTheme();
  const { can } = usePermissions();
  const canView = can('scripts.view');
  const canManage = can('scripts.manage');

  

  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [error, setError] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [blankDialogOpen, setBlankDialogOpen] = useState(false);
  const [blankTitle, setBlankTitle] = useState('');

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectScripts(projectId);
      setScripts(data);
    } catch (err) {
      console.error('Error fetching scripts:', err);
      setError(err.message || 'Failed to fetch scripts');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchScripts();
    } else {
      setError('No project selected');
    }
  }, [projectId, fetchScripts]);

  const handleUploadComplete = (newScript) => {
    fetchScripts();
    setSelectedScript(newScript);
    try {
      window.dispatchEvent(new CustomEvent('scriptsUpdated', {
        detail: { projectId, scriptId: newScript?._id }
      }));
    } catch (e) {
      console.warn('Unable to dispatch scriptsUpdated event', e);
    }
  };

  const handleScriptClick = (script) => {
    setSelectedScript(script);


    // Call the callback if provided
    if (onScriptSelect && typeof onScriptSelect === 'function') {
      onScriptSelect(script);
    }
  };

  const handleUploadClick = () => {
    if (!projectId) {
      console.error('No projectId available');
      setError('No project selected');
      return;
    }
    setUploadDialogOpen(true);
  };

  const handleUploadClose = () => {
    setUploadDialogOpen(false);
  };

  const handleBlankScriptClick = () => {
    if (!projectId) {
      setError('No project selected');
      return;
    }
    setBlankTitle('Untitled Script');
    setBlankDialogOpen(true);
  };

  const handleBlankDialogClose = () => {
    setBlankDialogOpen(false);
    setBlankTitle('');
  };

  const handleCreateBlankScript = async () => {
    try {
      if (!blankTitle.trim()) {
        setError('Please enter a title for the new script');
        return;
      }
      setLoading(true);
      setError(null);
      const newScript = await createBlankScript({
        projectId,
        title: blankTitle.trim()
      });
      await fetchScripts();
      setSelectedScript(newScript);
      handleBlankDialogClose();
    } catch (err) {
      console.error('Error creating blank script:', err);
      setError(typeof err === 'string' ? err : err?.message || 'Failed to create script');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (script, event) => {
    event.stopPropagation();
    setScriptToDelete(script);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      

      await deleteScript(scriptToDelete._id, projectId);

      

      // Clear localStorage cache for scene order
      try {
        localStorage.removeItem(`sceneOrder_${projectId}`);
      } catch (err) {
        console.error('Error clearing localStorage:', err);
      }

      

      // If the deleted script was selected, clear the selection
      if (selectedScript?._id === scriptToDelete._id) {
        setSelectedScript(null);
      }

      

      // Refresh the script list
      await fetchScripts();
      setDeleteDialogOpen(false);
      setScriptToDelete(null);
    } catch (err) {
      console.error('Error deleting script:', err);
      setError(typeof err === 'string' ? err : 'Failed to delete script. Please try again.');
      // Keep the dialog open if there's an error
      setDeleteDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setScriptToDelete(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle switching to analysis tab
  const handleSwitchToAnalysis = () => {
    setActiveTab(1);
  };

  // Add tab labels
  const tabLabels = ['Scripts', 'Analysis'];

  if (!projectId) {
    return (
      <Alert severity="warning">
        Please select a project to manage scripts
      </Alert>
    );
  }

  // Permission check - show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          You don't have permission to view scripts. Contact your project administrator for access.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        px: 2
      }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 0.5,
        px: 1
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary
          }}>
            Scripts
          </Typography>
          <Tabs 
            value={Math.min(activeTab, tabLabels.length - 1)} 
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: 1
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                minHeight: 36,
                minWidth: 70,
                py: 0.25,
                px: 1
              }
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Upload script" arrow>
            <IconButton
              onClick={handleUploadClick}
              disabled={!projectId || !canManage}
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New blank script" arrow>
            <IconButton
              onClick={handleBlankScriptClick}
              disabled={!projectId || !canManage}
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '200px' 
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && (
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              height: 'calc(100vh - 40px)',
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.3)
                : alpha(theme.palette.background.paper, 0.5),
              p: 3,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.15)'
                : '0 1px 4px rgba(0,0,0,0.04)'
            }}>
              <Paper sx={{ 
                flex: 1, 
                maxWidth: 350,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.6)
                  : theme.palette.background.paper,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.15)'
                  : '0 1px 4px rgba(0,0,0,0.04)'
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    p: 2,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  Script Versions
                </Typography>

                

                <Box sx={{ overflow: 'auto', flex: 1 }}>
                  {scripts.length === 0 ? (
                    <Box sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      color: theme.palette.text.secondary,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <ScriptIcon sx={{ 
                        fontSize: 48, 
                        mb: 2, 
                        opacity: 0.5,
                        color: theme.palette.primary.main
                      }} />
                      <Typography variant="subtitle1" sx={{ 
                        mb: 1,
                        fontWeight: 600
                      }}>
                        No scripts uploaded yet
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        maxWidth: '80%',
                        mb: 2
                      }}>
                        Click the Upload button to add your first script
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadClick}
                        disabled={!projectId || !canManage}
                        sx={{
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        Upload Script
                      </Button>
                    </Box>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {scripts.map((script, index) => (
                        <React.Fragment key={script._id}>
                          {index > 0 && <Divider />}
                          <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleScriptClick(script)}
                            selected={selectedScript?._id === script._id}
                            sx={{
                              py: 1.5,
                              px: 2,
                              transition: 'all 0.2s ease',
                              position: 'relative',
                              '&::after': selectedScript?._id === script._id ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '10%',
                                height: '80%',
                                width: '4px',
                                backgroundColor: theme.palette.info.main,
                                borderRadius: '0 4px 4px 0',
                                boxShadow: theme.palette.mode === 'dark'
                                  ? `0 0 8px ${alpha(theme.palette.info.main, 0.6)}`
                                  : 'none',
                              } : {},
                              '&.Mui-selected': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.info.main, 0.15)
                                  : alpha(theme.palette.info.main, 0.08),
                                backdropFilter: 'blur(4px)',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.info.main, 0.25)
                                    : alpha(theme.palette.info.main, 0.12),
                                }
                              },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.background.paper, 0.3)
                                  : alpha(theme.palette.background.paper, 0.6),
                              }
                            }}
                          >
                            <ListItemText
                              disableTypography
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {script.mimeType === 'application/pdf' ? (
                                    <PdfIcon fontSize="small" color="error" />
                                  ) : (
                                    <FileIcon fontSize="small" color="primary" />
                                  )}
                                  <Typography 
                                    variant="body1"
                                    sx={{ 
                                      fontWeight: selectedScript?._id === script._id ? 'medium' : 'normal',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {script.title}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      Last modified {new Date(script.updatedAt).toLocaleDateString()}
                                    </Typography>
                                    {/* Revision Color Indicator */}
                                    {script.versions && script.versions.length > 0 && (
                                      <Chip
                                        label={script.versions[script.versions.length - 1].revisionColor || 'White'}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.65rem',
                                          fontWeight: 600,
                                          backgroundColor: (() => {
                                            const color = script.versions[script.versions.length - 1].revisionColor;
                                            switch(color) {
                                              case 'Blue': return alpha('#2196f3', 0.15);
                                              case 'Pink': return alpha('#e91e63', 0.15);
                                              case 'Yellow': return alpha('#ffeb3b', 0.3);
                                              case 'Green': return alpha('#4caf50', 0.15);
                                              case 'Goldenrod': return alpha('#daa520', 0.15);
                                              default: return alpha(theme.palette.common.white, 0.1);
                                            }
                                          })(),
                                          color: (() => {
                                            const color = script.versions[script.versions.length - 1].revisionColor;
                                            switch(color) {
                                              case 'Blue': return '#2196f3';
                                              case 'Pink': return '#e91e63';
                                              case 'Yellow': return '#f57f17';
                                              case 'Green': return '#4caf50';
                                              case 'Goldenrod': return '#b8860b';
                                              default: return theme.palette.text.secondary;
                                            }
                                          })(),
                                          border: '1px solid ' + (() => {
                                            const color = script.versions[script.versions.length - 1].revisionColor;
                                            switch(color) {
                                              case 'Blue': return alpha('#2196f3', 0.3);
                                              case 'Pink': return alpha('#e91e63', 0.3);
                                              case 'Yellow': return alpha('#ffeb3b', 0.5);
                                              case 'Green': return alpha('#4caf50', 0.3);
                                              case 'Goldenrod': return alpha('#daa520', 0.3);
                                              default: return theme.palette.divider;
                                            }
                                          })(),
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Tooltip title={canManage ? "Delete Script" : "No permission to delete"}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleDeleteClick(script, e)}
                                        disabled={loading || !canManage}
                                        sx={{ 
                                          color: theme.palette.mode === 'dark'
                                            ? theme.palette.error.light
                                            : theme.palette.error.main,
                                          transition: 'all 0.2s ease',
                                          '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                              ? alpha(theme.palette.error.main, 0.2)
                                              : alpha(theme.palette.error.main, 0.1),
                                            transform: 'scale(1.1)'
                                          },
                                          '&:active': {
                                            transform: 'scale(0.95)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              </Paper>

              <Paper sx={{ 
                flex: 2,
                overflow: 'hidden',
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.6)
                  : theme.palette.background.paper,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.15)'
                  : '0 1px 4px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {selectedScript ? (
                  <ScriptViewer 
                    script={selectedScript} 
                    onBack={() => setSelectedScript(null)}
                    onAnalyzeScript={handleSwitchToAnalysis}
                  />
                ) : (
                  <Box sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: theme.palette.text.secondary
                  }}>
                    <ScriptIcon sx={{ 
                      fontSize: 48, 
                      mb: 2, 
                      opacity: 0.5,
                      color: theme.palette.primary.main
                    }} />
                    <Typography variant="subtitle1" sx={{ 
                      mb: 1,
                      fontWeight: 600
                    }}>
                      No Script Selected
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      maxWidth: '80%'
                    }}>
                      Select a script from the list to view its content
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ 
              height: 'calc(100vh - 40px)',
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.3)
                : alpha(theme.palette.background.paper, 0.5),
              p: 3,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.15)'
                : '0 1px 4px rgba(0,0,0,0.04)',
              overflow: 'auto'
            }}>
              <ScreenplayAnalysisManager 
                projectId={projectId} 
                projectName={projectName}
                selectedScript={selectedScript}
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 16px rgba(0,0,0,0.3)'
              : '0 4px 16px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="delete-dialog-title"
          sx={{
            borderBottom: `1px solid ${theme.palette.mode === 'dark'
              ? alpha(theme.palette.divider, 0.15)
              : theme.palette.divider}`,
            pb: 2,
            pt: 2.5,
            px: 3,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.8)
              : alpha(theme.palette.background.paper, 0.9),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ 
              color: theme.palette.mode === 'dark'
                ? theme.palette.error.light
                : theme.palette.error.main,
              fontSize: 28,
              filter: theme.palette.mode === 'dark'
                ? 'drop-shadow(0 0 6px rgba(244, 67, 54, 0.4))'
                : 'none'
            }} />
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.9)
                : theme.palette.text.primary,
              letterSpacing: '0.02em',
            }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, px: 3, py: 2.5 }}>
          <DialogContentText id="delete-dialog-description" sx={{
            color: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.7)
              : theme.palette.text.secondary,
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Are you sure you want to delete <strong>"{scriptToDelete?.title}"</strong>? This action cannot be undone.
            All versions of this script will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.4)
            : alpha(theme.palette.background.default, 0.7) }}>
          <Button 
            onClick={handleDeleteCancel}
            disabled={loading}
            sx={{
              minWidth: 120,
              margin: '4px',
              padding: '10px 20px',
              borderRadius: 2, // Standardized 8px
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: '0.5px',
              transition: 'all 0.3s ease',
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.divider, 0.2)}` 
                : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.4)
                : alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(5px)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.6)
                  : alpha(theme.palette.background.paper, 0.9),
                transform: 'translateY(-2px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 5px 15px ${alpha(theme.palette.common.black, 0.3)}`
                  : '0 5px 15px rgba(0,0,0,0.1)'
              },
              '&:active': {
                transform: 'translateY(1px)',
                boxShadow: 'none'
              },
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.8)
                : theme.palette.text.primary
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{
              minWidth: 120,
              margin: '4px',
              padding: '8px 16px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: '0.5px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.error.dark
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ScriptUploader
        open={uploadDialogOpen}
        onClose={handleUploadClose}
        onUploadComplete={handleUploadComplete}
        projectId={projectId}
      />

      <Dialog open={blankDialogOpen} onClose={handleBlankDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Create Blank Script</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Start a fresh Fountain screenplay. Leave content empty to use the default FADE IN template.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={blankTitle}
            onChange={(e) => setBlankTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBlankDialogClose}>Cancel</Button>
          <Button onClick={handleCreateBlankScript} variant="contained" disabled={loading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};

export default ScriptManager;

